import React, { useId, useState } from 'react';
import type { Feature } from 'geojson';
import { DebounceInput } from 'react-debounce-input';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const LATITUDE_MIN = -90;
const LATITUDE_MAX = 90;
const LONGITUDE_MIN = -180;
const LONGITUDE_MAX = 180;
const DEBOUNCE_MS = 300;

// The library's generics reject a forwardRef `element`; it renders one fine.
const DebouncedInput = DebounceInput as unknown as React.ComponentType<
  React.ComponentProps<'input'> & {
    element: typeof Input;
    debounceTimeout: number;
  }
>;

const deriveLatLngStrings = (
  centroid: Feature | undefined
): { lat: string; lng: string } => {
  if (centroid?.geometry?.type !== 'Point') return { lat: '', lng: '' };
  const [lng, lat] = centroid.geometry.coordinates as [number, number];
  return { lat: lat.toString(), lng: lng.toString() };
};

const isInRange = (n: number, min: number, max: number): boolean =>
  !isNaN(n) && n >= min && n <= max;

export type LatLngCoords = { latitude: number; longitude: number };

export type LatLngInputPairProps = {
  centroid: Feature | undefined;
  onChange: (coords: LatLngCoords) => void;
  readOnly?: boolean;
};

const LatLngInputPairInner: React.FC<LatLngInputPairProps> = ({
  centroid,
  onChange,
  readOnly = false,
}) => {
  const baseId = useId();
  const latId = `${baseId}-lat`;
  const lngId = `${baseId}-lng`;
  const latErrorId = `${latId}-error`;
  const lngErrorId = `${lngId}-error`;

  const { lat: initialLat, lng: initialLng } = deriveLatLngStrings(centroid);
  const [localLat, setLocalLat] = useState<string>(initialLat);
  const [localLng, setLocalLng] = useState<string>(initialLng);

  const latNum = parseFloat(localLat);
  const lngNum = parseFloat(localLng);
  const latValid = isInRange(latNum, LATITUDE_MIN, LATITUDE_MAX);
  const lngValid = isInRange(lngNum, LONGITUDE_MIN, LONGITUDE_MAX);
  const latError =
    localLat !== '' && !latValid
      ? `Latitude should be between ${LATITUDE_MIN} and ${LATITUDE_MAX}`
      : null;
  const lngError =
    localLng !== '' && !lngValid
      ? `Longitude should be between ${LONGITUDE_MIN} and ${LONGITUDE_MAX}`
      : null;

  const handleLatChange = (value: string) => {
    setLocalLat(value);
    const next = parseFloat(value);
    if (isInRange(next, LATITUDE_MIN, LATITUDE_MAX) && lngValid) {
      onChange({ latitude: next, longitude: lngNum });
    }
  };

  const handleLngChange = (value: string) => {
    setLocalLng(value);
    const next = parseFloat(value);
    if (isInRange(next, LONGITUDE_MIN, LONGITUDE_MAX) && latValid) {
      onChange({ latitude: latNum, longitude: next });
    }
  };

  return (
    <div className="tw:mb-3 tw:grid tw:gap-3 tw:md:grid-cols-2">
      <div className="tw:space-y-2">
        <Label htmlFor={latId}>Lat</Label>
        <DebouncedInput
          element={Input}
          id={latId}
          name="lat"
          type="number"
          step="any"
          min={LATITUDE_MIN}
          max={LATITUDE_MAX}
          placeholder="Lat"
          debounceTimeout={DEBOUNCE_MS}
          value={localLat}
          onChange={(e) => handleLatChange(e.target.value)}
          disabled={readOnly}
          data-testid="lat-input"
          aria-invalid={latError !== null}
          aria-describedby={latError ? latErrorId : undefined}
        />
        {latError && (
          <p id={latErrorId} className="tw:text-xs tw:text-destructive">
            {latError}
          </p>
        )}
      </div>
      <div className="tw:space-y-2">
        <Label htmlFor={lngId}>Lng</Label>
        <DebouncedInput
          element={Input}
          id={lngId}
          name="lng"
          type="number"
          step="any"
          min={LONGITUDE_MIN}
          max={LONGITUDE_MAX}
          placeholder="Lng"
          debounceTimeout={DEBOUNCE_MS}
          value={localLng}
          onChange={(e) => handleLngChange(e.target.value)}
          disabled={readOnly}
          data-testid="lng-input"
          aria-invalid={lngError !== null}
          aria-describedby={lngError ? lngErrorId : undefined}
        />
        {lngError && (
          <p id={lngErrorId} className="tw:text-xs tw:text-destructive">
            {lngError}
          </p>
        )}
      </div>
    </div>
  );
};

export const LatLngInputPair: React.FC<LatLngInputPairProps> = (props) => {
  const { lat, lng } = deriveLatLngStrings(props.centroid);
  return <LatLngInputPairInner key={`${lat},${lng}`} {...props} />;
};
